import { useState, useEffect, useCallback } from 'react';

export interface Toast {
  id: string;
  name: string;
  outboxId?: string;
  scheduledSendAt?: string;
}

interface UndoToastProps {
  toasts: Toast[];
  onUndo: (outboxId: string) => void;
  onDismiss: (id: string) => void;
}

function SingleToast({
  toast,
  onUndo,
  onDismiss,
}: {
  toast: Toast;
  onUndo: (outboxId: string) => void;
  onDismiss: (id: string) => void;
}) {
  const calculateRemaining = useCallback(() => {
    if (!toast.scheduledSendAt) return 0;
    return Math.max(0, Math.ceil((new Date(toast.scheduledSendAt).getTime() - Date.now()) / 1000));
  }, [toast.scheduledSendAt]);
  const [remaining, setRemaining] = useState(calculateRemaining);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const update = () => setRemaining(calculateRemaining());
    update();
    const interval = setInterval(update, 250);
    return () => clearInterval(interval);
  }, [calculateRemaining]);

  useEffect(() => {
    if (!toast.outboxId || remaining > 0) return;
      setFading(true);
      const t = setTimeout(() => onDismiss(toast.id), 300);
      return () => clearTimeout(t);
  }, [remaining, toast.id, toast.outboxId, onDismiss]);

  useEffect(() => {
    if (toast.outboxId) return;
    const timeout = setTimeout(() => onDismiss(toast.id), 4_000);
    return () => clearTimeout(timeout);
  }, [toast.id, toast.outboxId, onDismiss]);

  const handleUndo = useCallback(() => {
    if (toast.outboxId) {
      onUndo(toast.outboxId);
    }
    onDismiss(toast.id);
  }, [onUndo, onDismiss, toast.outboxId, toast.id]);

  return (
    <div
      className={`undo-toast flex items-center gap-3 rounded-xl bg-gray-900 px-5 py-3 text-white shadow-lg transition-all duration-300 ${
        fading ? 'translate-y-2 opacity-0' : 'translate-y-0 opacity-100'
      }`}
    >
      <span className="text-sm">
        {toast.outboxId ? <>Sent to <strong>{toast.name}</strong></> : <strong>{toast.name}</strong>}
      </span>
      {toast.outboxId && remaining > 0 && (
        <button
          onClick={handleUndo}
          className="ml-2 rounded-lg bg-white/20 px-3 py-1.5 text-sm font-medium text-white hover:bg-white/30 active:bg-white/40 transition-colors"
          style={{ minHeight: '36px' }}
        >
          Undo &middot; {remaining}s
        </button>
      )}
    </div>
  );
}

export default function UndoToast({ toasts, onUndo, onDismiss }: UndoToastProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 flex-col gap-2">
      {toasts.map((t) => (
        <SingleToast key={t.id} toast={t} onUndo={onUndo} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
